import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

export default function AdminPromoReports() {
  const [promoUsers, setPromoUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEarlyBirds: 0,
    activePromos: 0,
    totalFreeListingsUsed: 0,
    totalFreeListingsRemaining: 0
  });

  useEffect(() => {
    fetchPromoData();
  }, []);

  const fetchPromoData = async () => {
    setLoading(true);
    try {
      // Fetch Early Bird promo users
      const promoQuery = query(
        collection(db, 'earlyBirdPromos'),
        orderBy('createdAt', 'desc')
      );
      const promoSnapshot = await getDocs(promoQuery);
      const promoData = promoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        promoExpiresAt: doc.data().promoExpiresAt?.toDate()
      }));

      setPromoUsers(promoData);

      // Calculate stats
      const totalEarlyBirds = promoData.length;
      const activePromos = promoData.filter(user => user.promoActive).length;
      const totalFreeListingsUsed = promoData.reduce((sum, user) => sum + (user.usedListings || 0), 0);
      const totalFreeListingsRemaining = promoData.reduce((sum, user) => sum + (user.freeListings - (user.usedListings || 0)), 0);

      setStats({
        totalEarlyBirds,
        activePromos,
        totalFreeListingsUsed,
        totalFreeListingsRemaining
      });

    } catch (error) {
      console.error('Error fetching promo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('hu-HU') + ' ' + date.toLocaleTimeString('hu-HU');
  };

  const getStatusBadge = (user) => {
    const now = new Date();
    const expired = user.promoExpiresAt && now > user.promoExpiresAt;
    const remainingListings = user.freeListings - (user.usedListings || 0);

    if (expired) {
      return <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">Lejárt</span>;
    }
    if (!user.promoActive) {
      return <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">Inaktív</span>;
    }
    if (remainingListings <= 0) {
      return <span className="px-2 py-1 bg-yellow-500 text-white text-xs rounded-full">Elfogyott</span>;
    }
    return <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">Aktív</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937] mx-auto mb-4"></div>
          <p className="text-gray-600">Promóciós adatok betöltése...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Early Bird Promóciós Riportok</h1>
          <p className="text-gray-600">Korai regisztrációs kampány statisztikái és felhasználói adatok</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <span className="text-2xl">🐦</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Összes Early Bird</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEarlyBirds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <span className="text-2xl">✅</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aktív promóciók</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activePromos}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <span className="text-2xl">📊</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Felhasznált ingyenes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalFreeListingsUsed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <span className="text-2xl">🎁</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Maradék ingyenes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalFreeListingsRemaining}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Early Bird Felhasználók</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Felhasználó
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ingyenes hirdetések
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Regisztráció
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lejárat
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {promoUsers.map((user) => {
                  const remainingListings = user.freeListings - (user.usedListings || 0);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-[#e31937] flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || 'Névtelen'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {user.userId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{user.usedListings || 0}</span> / {user.freeListings} felhasználva
                        </div>
                        <div className="text-sm text-gray-500">
                          {remainingListings} maradt
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.promoExpiresAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {promoUsers.length === 0 && (
            <div className="text-center py-12">
              <span className="text-4xl mb-4 block">🐦</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Még nincsenek Early Bird felhasználók</h3>
              <p className="text-gray-500">A promóciós kampány indítása után itt jelennek meg a regisztrált felhasználók.</p>
            </div>
          )}
        </div>

        {/* Export/Actions */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={fetchPromoData}
            className="bg-[#e31937] hover:bg-[#b40000] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            🔄 Frissítés
          </button>
        </div>
      </div>
    </div>
  );
}
